/**
 * Alltag vs Labor auf den Offen/Aufgaben-Karten.
 * Telefon (max-width 600px) malt nur Menschensprache.
 * Desktop darf die Labor-Zeile behalten.
 */
const NODE_INTERNAL = /node:internal\b/;
const FILE_FRAME = /\.(?:ts|js|mjs|cjs):\d+:\d+/;
const ERR_CODE =
  /\b(?:ENOENT|EACCES|ECONNREFUSED|ECONNRESET|ETIMEDOUT|spawn |process-error)\b/i;
const BRACKET_MOD = /^\s*\[[a-z][a-z0-9._-]*\]\s+/i;
const STACK_START = /^\s*at\s+\S/;
const ERROR_LINE = /\bError:\s+\S/;

/** Interne Fehlerzeile, Stackframe, Ticket-Nummer allein. */
export function istLaborText(s: string): boolean {
  const t = s.trim();
  if (!t) return false;
  if (/^#\d+$/.test(t)) return true;
  if (NODE_INTERNAL.test(t)) return true;
  if (FILE_FRAME.test(t)) return true;
  if (ERR_CODE.test(t)) return true;
  if (STACK_START.test(t)) return true;
  if (BRACKET_MOD.test(t)) return true;
  if (ERROR_LINE.test(t) && /[\[\]\/]|failed|spawn|node:/.test(t)) return true;
  if (/\bat\s+[A-Za-z_$][\w.$]*\s+\([^)]+\)/.test(t)) return true;
  return false;
}

function knappeLeer(text: string): string {
  return text.replace(/\n{3,}/g, "\n\n").trim();
}

/** Titel: Menschensprache vorne, Labor-Suffix hinten. */
export function teileTitel(titel: string): { alltag: string; labor: string } {
  const roh = titel.replace(/\s+/g, " ").trim();
  if (!roh) return { alltag: "", labor: "" };

  const ohneId = roh.replace(/^#\d+\s+/, "");

  const prefix =
    /^(Vorschlag:\s*Wiederkehrender Fehler)\s*:\s+([\s\S]+)$/i.exec(
      ohneId,
    );
  if (prefix && istLaborText(prefix[2]!)) {
    return { alltag: prefix[1]!, labor: prefix[2]!.trim() };
  }

  const marker = /\s+(at\s+[A-Za-z_$]|\[[a-z][a-z0-9._-]*\]\s|Error:\s)/i.exec(
    ohneId,
  );
  if (marker && marker.index >= 8) {
    const rest = ohneId.slice(marker.index).trim();
    if (istLaborText(rest)) {
      return {
        alltag: ohneId.slice(0, marker.index).replace(/[:\s]+$/, ""),
        labor: rest,
      };
    }
  }

  if (istLaborText(ohneId)) {
    return { alltag: "Etwas ist schiefgegangen", labor: ohneId };
  }
  return { alltag: ohneId, labor: "" };
}

/**
 * Zeigt die Karte ihren Status an?
 *
 * Martin, 26.08. (Foto der Offen-Seite): jede Karte trug ZWEI Abzeichen —
 * `HIGH` und `OFFEN`. Auf dem Reiter „Aufgaben" ist aber alles offen; das
 * zweite Abzeichen wiederholt nur, was die Überschrift schon sagt, und tut
 * das in jeder Zeile.
 *
 * Information trägt der Status erst, wenn er vom Erwartbaren abweicht:
 * „in Arbeit" heißt, jemand ist dran; „erledigt" heißt, es ist vorbei.
 * „offen" heißt nichts, was man nicht schon wüsste.
 */
export function zeigeStatusAbzeichen(status: string | null | undefined): boolean {
  const s = (status || "open").toLowerCase();
  return s !== "open";
}

/**
 * Die Zeitangabe einer Karte: was in der Liste steht, was beim Verweilen.
 *
 * Vorher stand beides nebeneinander in derselben Zeile:
 *   „vor 6h · 26.08.2026, 04:01"
 * Beim Überfliegen zählt „vor 6h" — das Datum ist die Antwort auf eine Frage,
 * die man erst stellt, wenn einen die Zeile interessiert. Es bleibt erreichbar
 * (als `title` an der Zeile), nur nicht mehr in der Liste.
 *
 * @param relativ  z. B. "vor 6h" (aus relativeTime)
 * @param absolut  z. B. "26.08.2026, 04:01" (aus fmtDateTime)
 * @param erledigt absolutes Erledigt-Datum, falls vorhanden
 */
export function karteZeit(
  relativ: string,
  absolut: string,
  erledigt = "",
): { sichtbar: string; beiVerweilen: string } {
  const rel = (relativ || "").trim();
  const abs = (absolut || "").trim();
  const erl = (erledigt || "").trim();

  // Ohne Relativzeit ist das Datum das Einzige, was wir haben — dann zeigt es
  // sich, statt die Zeile leer zu lassen.
  const sichtbar = rel || abs;

  const teile = [abs && `angelegt ${abs}`, erl && `erledigt ${erl}`].filter(
    Boolean,
  );
  return { sichtbar, beiVerweilen: teile.join(" · ") };
}

/** Beschreibung: Alltag-Zeilen und Labor-Zeilen getrennt. */
export function teileAlltagLabor(text: string): {
  alltag: string;
  labor: string;
} {
  const alltag: string[] = [];
  const labor: string[] = [];
  for (const raw of text.split("\n")) {
    const t = raw.trimEnd();
    if (!t.trim()) {
      alltag.push("");
      continue;
    }
    const bel = /^BELEG:\s*([\s\S]*)$/i.exec(t.trim());
    if (bel && istLaborText(bel[1]!)) {
      labor.push(t.trim());
      continue;
    }
    if (istLaborText(t)) {
      labor.push(t.trim());
      continue;
    }
    alltag.push(t);
  }
  return { alltag: knappeLeer(alltag.join("\n")), labor: knappeLeer(labor.join("\n")) };
}
